'use client'

import { CheckCircle, ChevronDown, Download, ExternalLink, FileText, HardDrive } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleDriveCreateFileFromText } from 'sprites-types/workflow/tools/googleDriveCreateFileFromText'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleDriveCreateFileFromTextDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [isFileInputExpanded, setIsFileInputExpanded] = useState(false)
  const schema = getToolResultSchema(googleDriveCreateFileFromText, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const fileId = result.response?.id
  const googleDriveUrl = result.response?.googleDriveUrl
  const fileUrl =
    googleDriveUrl || (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null)
  const cdnUrl = result.response?.cdnUrl

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex-shrink-0">
          <div className="rounded-full bg-green-100 p-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-blue-500" />
            <div className="text-sm font-semibold text-gray-900">File created in Google Drive</div>
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
            {fileUrl && (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="font-medium">Google Drive URL</span>
              </a>
            )}
            {cdnUrl && (
              <a
                href={cdnUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-green-600 hover:text-green-800"
              >
                <Download className="h-4 w-4" />
                <span className="font-medium">Direct Download</span>
              </a>
            )}
          </div>

          <div>
            {inputs.text && (
              <details className="w-full" open={isFileInputExpanded}>
                <summary
                  className="flex w-full cursor-pointer list-none items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                  onClick={(e) => {
                    e.preventDefault()
                    setIsFileInputExpanded(!isFileInputExpanded)
                  }}
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      isFileInputExpanded ? 'rotate-90' : ''
                    }`}
                  />
                  <span>File Content</span>
                </summary>
                {isFileInputExpanded && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
                    <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap text-xs text-gray-800">
                      {inputs.text}
                    </pre>
                  </div>
                )}
              </details>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <FileText className="h-5 w-5 text-gray-400" />
        </div>
      </div>
    </div>
  )
}

function downloadGoogleDriveCreateFile(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleDriveCreateFileFromTextToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleDriveCreateFileFromText, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const fileName = result.response?.name || inputs.name || inputs.filename || 'Untitled'
  const googleDriveUrl = result.response?.googleDriveUrl
  const fileUrl = googleDriveUrl
  const cdnUrl = result.response?.cdnUrl
  const textContent = inputs.text || inputs.content

  return `## File Created in Google Drive

**File Name:** ${fileName}
${fileUrl ? `**Google Drive URL:** ${fileUrl}` : ''}
${cdnUrl ? `**Direct Download:** ${cdnUrl}` : ''}
${textContent ? `**File Content:** \`\`\`\n${textContent}\n\`\`\`` : ''}

---
`
}

export const GoogleDriveCreateFileFromTextDisplay = createWorkflowToolComponent(
  GoogleDriveCreateFileFromTextDisplayComponent,
  downloadGoogleDriveCreateFile,
  googleDriveCreateFileFromTextToMarkdown
)
